import React, { memo } from 'react';
import { useSelector } from 'react-redux';

import { Box, TableCell, TableRow, Tooltip, useTheme } from '@mui/material';
import { InfoIcon } from '@sharedCR/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { SlaLevelType, SlaType } from 'constants/slaGroups';

import { selectAllRolesAndUsersById } from 'store/underwriting/selectors';

import { columns, LOAN_CONDITIONS_INFO_TABLE_COLUMN_ID } from './LoanConditionInfoTable.constants';

const getStyles = (theme, { isEligibleForSignOff }) => ({
  tableRowColor: {
    backgroundColor: isEligibleForSignOff ? theme.palette.success.main : null,
  },
  deletedUsers: {
    color: theme.palette.error.main,
  },
});

const disablePortalProp = { disablePortal: true };

const LoanConditionInfoTableItem = ({ slaLevel }) => {
  const { isEligibleForSignOff, deletedPotentialSignersFullNames, deletedActualSignersFullNames } =
    slaLevel;
  const theme = useTheme();
  const classes = getStyles(theme, { isEligibleForSignOff });

  const { value, loanConditionSlaLevelId } = slaLevel;
  const allRolesAndUsersById = useSelector(selectAllRolesAndUsersById);
  const slaLevelRoleName = allRolesAndUsersById[value]?.name;

  const isDeletedPotentialSigners = deletedPotentialSignersFullNames?.length > 0;
  const isDeletedActualSigners = deletedActualSignersFullNames?.length > 0;
  const isTypeUser = slaLevel?.type === SlaLevelType.USER;

  return (
    <TableRow data-testid={loanConditionSlaLevelId} sx={classes.tableRowColor}>
      {columns.map(({ id, align, className }) => {
        const isDeletedActualSignersTag =
          id === LOAN_CONDITIONS_INFO_TABLE_COLUMN_ID.ACTUAL_SIGNERS && isDeletedActualSigners;
        const isDeletedPotentialSignersTag =
          id === LOAN_CONDITIONS_INFO_TABLE_COLUMN_ID.POTENTIAL_SIGNERS &&
          isDeletedPotentialSigners;
        const isSlaColumn = id === LOAN_CONDITIONS_INFO_TABLE_COLUMN_ID.SLA;
        const value = isSlaColumn
          ? `${slaLevel.slaDuration.duration} ${
              slaLevel.slaDuration.type === SlaType.REGULAR_HOURS ? 'Hours' : 'Days'
            }`
          : slaLevel[id];

        return (
          <TableCell align={align} className={className} key={id}>
            <span>{value}</span>
            {isDeletedActualSignersTag && (
              <Box sx={classes.deletedUsers}>Deleted: {deletedActualSignersFullNames}</Box>
            )}
            {isDeletedPotentialSignersTag && (
              <Box sx={classes.deletedUsers}>Deleted: {deletedPotentialSignersFullNames}</Box>
            )}
            {id === LOAN_CONDITIONS_INFO_TABLE_COLUMN_ID.TYPE && !isTypeUser && (
              <Tooltip
                PopperProps={disablePortalProp}
                className="ml-1"
                placement="top"
                title={slaLevelRoleName}
              >
                <span>
                  <InfoIcon size={IconSize[16]} />
                </span>
              </Tooltip>
            )}
          </TableCell>
        );
      })}
    </TableRow>
  );
};

LoanConditionInfoTableItem.propTypes = {
  slaLevel: PropTypes.object.isRequired,
};

export default memo(LoanConditionInfoTableItem);
